import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const PendingLeaveList = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [filterStatus, setFilterStatus] = useState("PENDING_FORWARDED");
  const [filterLeaveType, setFilterLeaveType] = useState("");
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [monthFilter, setMonthFilter] = useState("thisMonth");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const navbarColor = sessionStorage.getItem("navbarColor") || "#080235";
  const navigate = useNavigate();

  // ===== Fetch ONLY Pending Leaves =====
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    // Fetch only pending leaves
    fetch(`${API_BASE_URL}/api/leaves/all`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if ([401, 403].includes(res.status)) {
          navigate("/");
          throw new Error("Unauthorized");
        }
        return res.json();
      })
      .then((data) => {
        setLeaves(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching leaves:", err);
        setLoading(false);
      });

    // Fetch leave types
    fetch(`${API_BASE_URL}/api/leave-types/all`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setLeaveTypes(data))
      .catch((err) => console.error("Error fetching leave types:", err));
  }, [navigate]);

  // ===== Date Validation =====
  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      const from = new Date(dateRange.start);
      const to = new Date(dateRange.end);

      if (to < from) {
        toast.error("To Date must be greater than or equal to From Date");
        setDateRange((prev) => ({ ...prev, end: "" }));
      }
    }
  }, [dateRange.start, dateRange.end]);

  const getCurrentMonthRange = () => {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: new Date(now.getFullYear(), now.getMonth() + 1, 0),
    };
  };

  const getLastMonthRange = () => {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
      end: new Date(now.getFullYear(), now.getMonth(), 0),
    };
  };

  // ===== Approve Leave =====
  const handleApprove = (leaveId) => {
    const token = sessionStorage.getItem("token");
    fetch(`${API_BASE_URL}/api/leaves/${leaveId}/status?status=APPROVED`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((updatedLeave) => {
        setLeaves((prev) =>
          prev.map((leave) =>
            leave.id === updatedLeave.id
              ? { ...leave, status: updatedLeave.status, rejectionReason: null }
              : leave
          )
        );
      })
      .catch((err) => console.error("Error approving leave:", err));
  };

  // ===== Reject Leave =====
  const openRejectModal = (leaveId) => {
    setSelectedLeaveId(leaveId);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleRejectSubmit = () => {
    if (!rejectReason.trim()) {
      alert("Please provide a rejection reason.");
      return;
    }
    const token = sessionStorage.getItem("token");

    fetch(`${API_BASE_URL}/api/leaves/${selectedLeaveId}/status?status=REJECTED`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ rejectionReason: rejectReason }),
    })
      .then((res) => res.json())
      .then((updatedLeave) => {
        setLeaves((prev) =>
          prev.map((leave) => (leave.id === updatedLeave.id ? updatedLeave : leave))
        );
        setShowRejectModal(false);
        setRejectReason("");
        setSelectedLeaveId(null);
      })
      .catch((err) => console.error("Error rejecting leave:", err));
  };

  // ===== Sorting =====
  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up"></i>;
    return sortConfig.direction === "ascending" ? (
      <i className="bi bi-arrow-up"></i>
    ) : (
      <i className="bi bi-arrow-down"></i>
    );
  };

  // ===== Filtering & Sorting =====
  const filteredLeaves = leaves.filter((leave) => {
    const start = new Date(leave.startDate);
    const { start: monthStart, end: monthEnd } =
      monthFilter === "lastMonth" ? getLastMonthRange() : getCurrentMonthRange();

    const matchesMonth =
      !monthFilter || (start >= monthStart && start <= monthEnd);

    const matchesSearch = leave.employeeName
      .toLowerCase()
      .includes(searchName.toLowerCase());

    const matchesStatus =
  filterStatus === "PENDING_FORWARDED"
    ? (leave.status === "PENDING" || leave.status === "FORWARDED")
    : (filterStatus ? leave.status === filterStatus : true);

    const matchesType = filterLeaveType ? leave.leaveType === filterLeaveType : true;

    const matchesDateRange =
      (!dateRange.start || new Date(leave.startDate) >= new Date(dateRange.start)) &&
      (!dateRange.end || new Date(leave.endDate) <= new Date(dateRange.end));

    return (
      matchesMonth &&
      matchesSearch &&
      matchesStatus &&
      matchesType &&
      matchesDateRange
    );
  });

  const sortedLeaves = [...filteredLeaves].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const valueA = a[sortConfig.key];
    const valueB = b[sortConfig.key];
    if (valueA < valueB) return sortConfig.direction === "ascending" ? -1 : 1;
    if (valueA > valueB) return sortConfig.direction === "ascending" ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h5 className="mt-3">Loading leave requests...</h5>
      </div>
    );
  }

  return (
    <>
      <div className="fixed-top">
        <AdminNavbar />
      </div>

      <div className="container-fluid mt-5" style={{ paddingTop: "80px" }}>
        <h2 className="text-start mb-1">Pending Leave Requests</h2>

        {/* ==== Filters ==== */}
        <div className="row g-2 mb-3 align-items-end">
          <div className="col-md-2">
            <input
              type="text"
              className="form-control"
              placeholder="Search employee..."
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </div>

          <div className="col-md-1">
            <select
              className="form-select"
              value={filterLeaveType}
              onChange={(e) => setFilterLeaveType(e.target.value)}
            >
              <option value="">All Leave Types</option>
              {Array.isArray(leaveTypes) &&
                leaveTypes.map((type) => (
                  <option key={type.id || type} value={type.name || type}>
                    {type.name || type}
                  </option>
                ))}
            </select>
          </div>

          {/* <div className="col-md-1">
            <select
              className="form-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="FORWARDED">Forwarded</option>
            </select>
          </div> */}

          <div className="col-md-1">
            <label className="form-label mb-0">From</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.start}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, start: e.target.value }))
              }
            />
          </div>

          <div className="col-md-1">
            <label className="form-label mb-0">To</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.end}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, end: e.target.value }))
              }
            />
          </div>

          <div className="col-md-1">
            <select
              className="form-select"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
            >
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="">All Months</option>
            </select>
          </div>
        </div>

        {/* ==== Table ==== */}
        <div
          className="table-responsive"
          style={{ height: "75vh", overflowY: "auto", overflowX: "auto" }}
        >
          <table className="table table-striped align-middle text-start mb-0">
            <thead className="position-sticky top-0">
              <tr>
                <th
                  style={{ backgroundColor: "#DFE4EB", cursor: "pointer" }}
                  onClick={() => handleSort("employeeName")}
                >
                  Employee {sortIcon("employeeName")}
                </th>
                <th
                  style={{ backgroundColor: "#DFE4EB", cursor: "pointer" }}
                  onClick={() => handleSort("leaveType")}
                >
                  Leave Type {sortIcon("leaveType")}
                </th>
                <th
                  style={{ backgroundColor: "#DFE4EB", cursor: "pointer" }}
                  onClick={() => handleSort("startDate")}
                >
                  Start Date {sortIcon("startDate")}
                </th>
                <th
                  style={{ backgroundColor: "#DFE4EB", cursor: "pointer" }}
                  onClick={() => handleSort("endDate")}
                >
                  End Date {sortIcon("endDate")}
                </th>
                <th style={{ backgroundColor: "#DFE4EB" }}>Total Days</th>
                <th style={{ backgroundColor: "#DFE4EB" }}>Leave Reason</th>
                <th style={{ backgroundColor: "#DFE4EB" }}>Document</th>
                <th
                  style={{ backgroundColor: "#DFE4EB", cursor: "pointer" }}
                  onClick={() => handleSort("status")}
                >
                  Status {sortIcon("status")}
                </th>
                <th style={{ backgroundColor: "#DFE4EB" }}>Reason</th>
                <th style={{ backgroundColor: "#DFE4EB" }}>Approve | Reject</th>
              </tr>
            </thead>

            <tbody>
              {sortedLeaves.map((leave) => (
                <tr key={leave.id}>
                  <td>{leave.employeeName}</td>
                  <td>{leave.leaveType}</td>
                  <td>
                    {new Date(leave.startDate).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>
                  <td>
                    {new Date(leave.endDate).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>

                  <td>
                    {(() => {
                      const start = new Date(leave.startDate);
                      const end = new Date(leave.endDate);
                      const diff =
                        Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
                      return diff > 0 ? diff : 0;
                    })()}
                  </td>

                  <td>{leave.reason}</td>

                  <td>
                    {leave.supportiveDocument && (
                      <a
                        href={`${API_BASE_URL}/uploads/${leave.supportiveDocument}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        View
                      </a>
                    )}
                  </td>

                  <td>
                    <span className="text-dark fw-semibold">{leave.status}</span>
                  </td>

                  <td>{leave.rejectionReason || leave.cancelReason || ""}</td>

                  <td>
                    {(leave.status === "PENDING" ||
                      leave.status === "FORWARDED") && (
                      <div className="d-flex gap-2">
                        <span
                          className="text-primary"
                          style={{
                            cursor: "pointer",
                            textDecoration: "none",
                            fontWeight: 500,
                          }}
                          onClick={() => handleApprove(leave.id)}
                        >
                          Approve
                        </span>

                        <span
                          className="text-primary ms-2"
                          style={{
                            cursor: "pointer",
                            textDecoration: "none",
                            fontWeight: 500,
                          }}
                          onClick={() => openRejectModal(leave.id)}
                        >
                          Reject
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div
          className="modal show fade d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", paddingTop: "250px" }}
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div
                className="modal-header"
                style={{
                  backgroundColor: navbarColor,
                  color: "rgba(253, 252, 252, 1)",
                }}
              >
                <h5 className="modal-title">Reject Leave Request</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRejectModal(false)}
                ></button>
              </div>

              <div className="modal-body">
                <textarea
                  className="form-control"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter rejection reason..."
                  rows="4"
                />
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowRejectModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleRejectSubmit}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PendingLeaveList;
